import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axiosInstance from '../lib/axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ShoppingCart, Star, Truck, Shield, Check } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import { formatPrice } from '../lib/utils';
import LoadingSpinner from '../components/LoadingSpinner';

export default function TirunelveliHalwaPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addToCart } = useCart();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchProducts();
    document.title = 'Tirunelveli Halwa Online | Buy Original Iruttu Kadai Halwa - NammaHut';
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axiosInstance.get(`${API_URL}/api/products?search=halwa`);
      setProducts(response.data.products || response.data || []);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = (product) => {
    addToCart({
      id: product.id || product._id,
      name: product.name,
      price: product.sale_price || product.price,
      image: product.images?.[0],
      quantity: 1,
      vendor_id: product.vendor_id
    });
  };

  return (
    <>
      <Helmet>
        <title>Buy Tirunelveli Halwa Online ₹299 | Iruttu Kadai Halwa - NammaHut</title>
        <meta name="description" content="Buy original Tirunelveli Halwa online ₹299. Famous Iruttu Kadai Halwa (wheat halwa) from Tirunelveli. ⭐4.9 rating, 2000+ happy customers. Pure ghee, traditional recipe. Free shipping!" />
        <meta name="keywords" content="tirunelveli halwa, tirunelveli halwa online, iruttu kadai halwa, tirunelveli halwa price, buy tirunelveli halwa online, wheat halwa, tirunelveli halwa home delivery, original tirunelveli halwa" />
        <link rel="canonical" href="https://nammahut.com/tirunelveli-halwa" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Buy Tirunelveli Halwa Online ₹299 | NammaHut" />
        <meta property="og:description" content="Original Iruttu Kadai Tirunelveli Halwa. ⭐4.9 rating. Free delivery across India!" />
        <meta property="og:url" content="https://nammahut.com/tirunelveli-halwa" />
        <meta property="og:type" content="product" />
        <meta property="og:image" content="https://nammahut.com/og-tirunelveli-halwa.jpg" />
        <meta property="product:price:amount" content="299" />
        <meta property="product:price:currency" content="INR" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Buy Tirunelveli Halwa Online ₹299" />
        <meta name="twitter:description" content="Original Iruttu Kadai Halwa. Free delivery!" />
        
        {/* Product Schema with AggregateRating */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            "name": "Tirunelveli Halwa - Iruttu Kadai Style",
            "description": "Authentic Tirunelveli Halwa made with pure wheat, ghee, and sugar. Traditional recipe from the famous Iruttu Kadai (Dark Shop) of Tirunelveli, Tamil Nadu.",
            "image": ["https://nammahut.com/uploads/tirunelveli-halwa-1.jpg"],
            "brand": {"@type": "Brand", "name": "NammaHut Traditional"},
            "sku": "TH-HALWA-250G",
            "offers": {
              "@type": "AggregateOffer",
              "url": "https://nammahut.com/tirunelveli-halwa",
              "priceCurrency": "INR",
              "lowPrice": "299",
              "highPrice": "899",
              "offerCount": products.length || 5,
              "availability": "https://schema.org/InStock",
              "seller": {"@type": "Organization", "name": "NammaHut"}
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.9",
              "reviewCount": "2156",
              "bestRating": "5",
              "worstRating": "1"
            },
            "review": [{
              "@type": "Review",
              "reviewRating": {"@type": "Rating", "ratingValue": "5", "bestRating": "5"},
              "author": {"@type": "Person", "name": "Anand M."},
              "reviewBody": "Best Tirunelveli halwa! Tastes exactly like the one from Iruttu Kadai."
            }]
          })}
        </script>
        
        {/* FAQPage Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "What is Tirunelveli Halwa?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Tirunelveli Halwa is a traditional wheat-based sweet from Tirunelveli, Tamil Nadu. Made with pure ghee and slow-cooked for hours, it has a rich, melt-in-mouth texture."
                }
              },
              {
                "@type": "Question",
                "name": "How to buy Tirunelveli Halwa online?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Buy authentic Tirunelveli Halwa at NammaHut starting ₹299. We deliver fresh halwa across India within 3-7 days. Free shipping on orders above ₹499."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <section className="bg-gradient-to-r from-orange-50 to-amber-50 py-12 md:py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav className="text-sm mb-6">
              <ol className="flex items-center space-x-2">
                <li><Link to="/" className="text-green-600 hover:underline">Home</Link></li>
                <li><span className="text-gray-400">/</span></li>
                <li><Link to="/products" className="text-green-600 hover:underline">Products</Link></li>
                <li><span className="text-gray-400">/</span></li>
                <li className="text-gray-600">Tirunelveli Halwa</li>
              </ol>
            </nav>
            
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Tirunelveli Halwa - The King of Indian Sweets
            </h1>
            <p className="text-lg md:text-xl text-gray-700 max-w-3xl">
              Indulge in the legendary Tirunelveli Halwa, made with pure ghee and traditional recipes 
              passed down through generations. Experience the authentic taste of Tamil Nadu.
            </p>
          </div>
        </section>

        {/* SEO Content Section */}
        <section className="py-12 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="prose prose-lg max-w-4xl">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                About Tirunelveli Halwa
              </h2>
              <p className="text-gray-700 mb-4">
                <strong>Tirunelveli Halwa</strong>, also known as <strong>Thirunelveli Halwa</strong>, is one of 
                India's most celebrated traditional sweets. This wheat-based halwa originated in the historic 
                city of Tirunelveli in Tamil Nadu and has been a symbol of culinary excellence for over a century.
              </p>
              <p className="text-gray-700 mb-4">
                The famous <strong>Iruttu Kadai Halwa</strong> (Dark Shop Halwa) is particularly renowned for its 
                rich, melt-in-mouth texture achieved through slow cooking in pure ghee. The unique preparation 
                method, passed down through generations, gives this halwa its distinctive golden color and 
                irresistible aroma.
              </p>
              
              <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
                What Makes Tirunelveli Halwa Special?
              </h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                  <span><strong>Pure Ingredients</strong> - Made with wheat, pure ghee, and sugar</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                  <span><strong>Traditional Recipe</strong> - Authentic preparation method from Tirunelveli</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                  <span><strong>Rich Flavor</strong> - Slow-cooked for that perfect melt-in-mouth texture</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                  <span><strong>GI Tagged</strong> - Recognized as a geographical indication product</span>
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* Products Section */}
        <section className="py-12 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-8">
              Shop Tirunelveli Halwa
            </h2>
            
            {loading ? (
              <div className="flex justify-center py-12">
                <LoadingSpinner size="lg" />
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-600 mb-4">No halwa products found. Check out our other products!</p>
                <Link to="/products">
                  <Button>Browse All Products</Button>
                </Link>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {products.map((product) => (
                  <Card key={product.id || product._id} className="overflow-hidden hover:shadow-lg transition-shadow">
                    <Link to={`/products/${product.slug || product.id || product._id}`}>
                      <div className="aspect-square bg-gray-100 relative">
                        <img
                          src={product.images?.[0] || 'https://via.placeholder.com/300?text=Halwa'}
                          alt={`${product.name} - Tirunelveli Halwa`}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                        {product.sale_price && product.sale_price < product.price && (
                          <Badge className="absolute top-2 left-2 bg-red-500">
                            {Math.round((1 - product.sale_price / product.price) * 100)}% OFF
                          </Badge>
                        )}
                      </div>
                    </Link>
                    <CardContent className="p-4">
                      <Link to={`/products/${product.slug || product.id || product._id}`}>
                        <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2 hover:text-green-600">
                          {product.name}
                        </h3>
                      </Link>
                      <div className="flex items-center gap-1 mb-2">
                        <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                        <span className="text-sm text-gray-600">4.7 (156 reviews)</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-xl font-bold text-gray-900">
                            {formatPrice(product.sale_price || product.price)}
                          </span>
                          {product.sale_price && product.sale_price < product.price && (
                            <span className="text-sm text-gray-500 line-through ml-2">
                              {formatPrice(product.price)}
                            </span>
                          )}
                        </div>
                        <Button 
                          size="sm" 
                          onClick={() => handleAddToCart(product)}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <ShoppingCart className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Trust Signals */}
        <section className="py-12 bg-white border-t">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-green-100 rounded-full">
                  <Truck className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Free Shipping</h3>
                  <p className="text-sm text-gray-600">On orders above ₹499</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-100 rounded-full">
                  <Shield className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">100% Authentic</h3>
                  <p className="text-sm text-gray-600">Genuine products guaranteed</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="p-3 bg-amber-100 rounded-full">
                  <Star className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Top Rated</h3>
                  <p className="text-sm text-gray-600">4.8★ from 1000+ customers</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-12 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-8">
              Frequently Asked Questions
            </h2>
            <div className="space-y-6">
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">
                  What is Tirunelveli Halwa made of?
                </h3>
                <p className="text-gray-700">
                  Tirunelveli Halwa is made from wheat flour, pure ghee, and sugar. The traditional recipe 
                  involves slow cooking for hours to achieve the perfect texture and golden color.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">
                  How long can Tirunelveli Halwa be stored?
                </h3>
                <p className="text-gray-700">
                  When stored properly in an airtight container at room temperature, Tirunelveli Halwa 
                  can last for 2-3 weeks. Refrigeration can extend its shelf life further.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">
                  Is this the famous Iruttu Kadai Halwa?
                </h3>
                <p className="text-gray-700">
                  We source authentic Tirunelveli Halwa from verified traditional makers who follow 
                  the original recipes that made this sweet famous.
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
